import { CircleX } from "lucide-react";
import type {
  CSSProperties,
  FunctionComponent,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from "react";
import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import {
  controlBox,
  controlHeight,
  INNER_INPUT,
  useControlDefaults,
  type ControlSize,
  type ControlStatus,
} from "./control";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  size?: ControlSize;
  status?: ControlStatus;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Clear button; fires `onChange` with an empty value, like antd. */
  allowClear?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** Sets the value through the native setter so React's `onChange` fires as for user input. */
const clearNativeInput = (input: HTMLInputElement | null) => {
  if (!input) return;
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(input, "");
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.focus();
};

/** Text input (antd `Input`). Always renders a wrapper so prefix/suffix/clear share one box. */
export const Input: FunctionComponent<InputProps> = ({
  size: sizeProp,
  status: statusProp,
  id: idProp,
  prefix,
  suffix,
  allowClear,
  className,
  style,
  disabled,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const { size, status, id, aria } = useControlDefaults({ size: sizeProp, status: statusProp, id: idProp });
  const innerRef = useRef<HTMLInputElement | null>(null);
  const hasValue = props.value !== undefined ? String(props.value ?? "") !== "" : undefined;

  const setRefs = (node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <span
      className={cn(
        // shrink-0: a control keeps its height in a flex column that overflows
        "group/input inline-flex w-full min-w-0 shrink-0 items-center gap-1",
        controlBox(status, disabled),
        controlHeight[size],
        className
      )}
      style={style}
    >
      {prefix && <span className="inline-flex shrink-0 text-noa-muted">{prefix}</span>}
      <input ref={setRefs} id={id} {...aria} disabled={disabled} className={cn(INNER_INPUT, "h-full")} {...props} />
      {allowClear && !disabled && hasValue !== false && (
        <button
          type="button"
          aria-label={t("common:clear")}
          onClick={() => clearNativeInput(innerRef.current)}
          className="inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-noa-muted opacity-0 transition-opacity group-hover/input:opacity-100 group-focus-within/input:opacity-100 focus-visible:opacity-100 hover:text-noa"
        >
          <CircleX className="size-3.5" />
        </button>
      )}
      {suffix && <span className="inline-flex shrink-0 text-noa-muted">{suffix}</span>}
    </span>
  );
};

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  status?: ControlStatus;
  /** Grow with the content; `{ minRows, maxRows }` bounds it (CSS `field-sizing: content`). */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  ref?: Ref<HTMLTextAreaElement>;
}

// Browsers without `field-sizing: content` (older Safari/Firefox) would keep a fixed box: grow it by hand.
const growByHand = (node: HTMLTextAreaElement | null, grows: unknown) => {
  if (!grows || !node || CSS.supports("field-sizing", "content")) return;
  node.style.height = "auto";
  node.style.height = `${node.scrollHeight}px`;
};

/** Multi-line input (antd `Input.TextArea`). */
export const TextArea: FunctionComponent<TextAreaProps> = ({
  status: statusProp,
  id: idProp,
  autoSize,
  className,
  style,
  disabled,
  rows = 2,
  ref,
  onInput,
  ...props
}) => {
  const { status, id, aria } = useControlDefaults({ status: statusProp, id: idProp });
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  // A value set from code (cleared after send, a picked quick prompt) fires no `input` event: resize too.
  const { value } = props;
  const grows = Boolean(autoSize);
  useLayoutEffect(() => growByHand(innerRef.current, grows), [value, grows]);

  const setRefs = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    growByHand(node, autoSize);
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };
  const bounds = typeof autoSize === "object" ? autoSize : {};
  const autoSizeStyle: CSSProperties = autoSize
    ? {
        minHeight: `calc(${bounds.minRows ?? 1}lh + 10px)`,
        ...(bounds.maxRows ? { maxHeight: `calc(${bounds.maxRows}lh + 10px)` } : {}),
      }
    : {};

  return (
    <textarea
      ref={setRefs}
      id={id}
      {...aria}
      onInput={(event) => {
        growByHand(innerRef.current, autoSize);
        onInput?.(event);
      }}
      rows={autoSize ? undefined : rows}
      disabled={disabled}
      className={cn(
        "block w-full px-[11px] py-1 text-sm leading-[1.5715] outline-none placeholder:text-noa-disabled",
        controlBox(status, disabled),
        autoSize ? "resize-none [field-sizing:content]" : "resize-y",
        className
      )}
      style={{ ...autoSizeStyle, ...style }}
      {...props}
    />
  );
};
